/**
 * BridgePicker — reusable selector for a single graded bridge.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useMemo } from "react";
import { GradeVector } from "../../components/GradeVector.js";
import {
  formatGrade, type Phase, type SdpCode, } from "../../algorithms/grade.js";
import { PanelSurface } from "@csk/demo-ui";
import {
  buildBridge,
  PHASE_CHIPS_FOR,
  SDP_CHIPS,
  type BridgeChoice,
} from "./model.js";

interface BridgePickerProps {
  readonly title: string;
  readonly accent: string;
  readonly choice: BridgeChoice;
  readonly onSdp: (sdp: SdpCode) => void;
  readonly onPhase: (phase: Phase) => void;
}

export function BridgePicker({
  title,
  accent,
  choice,
  onSdp,
  onPhase,
}: BridgePickerProps): React.JSX.Element {
  const bridge = useMemo(() => buildBridge(choice), [choice]);
  return (
    <PanelSurface.Card glowColor={`${accent}33`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <PanelSurface.SectionHeading style={{ margin: 0 }}>{title}</PanelSurface.SectionHeading>
        <span
          className="font-mono text-[12.5px] font-bold"
          style={{ color: accent }}
        >
          {formatGrade(bridge.grade)}
        </span>
      </div>
      <div className="mt-3">
        <div className="mb-1.5 font-mono text-[11px] tracking-[0.06em] text-[var(--tcc-muted)]">
          SDP
        </div>
        <PanelSurface.ChipRow
          items={SDP_CHIPS}
          activeId={choice.sdp}
          onChange={(id) => {
            onSdp(id);
          }}
          ariaLabel={`${title} SDP`}
        />
      </div>
      <div className="mt-3.5">
        <div className="mb-1.5 font-mono text-[11px] tracking-[0.06em] text-[var(--tcc-muted)]">
          Phase
        </div>
        <PanelSurface.ChipRow
          items={PHASE_CHIPS_FOR(accent)}
          activeId={choice.phase}
          onChange={(id) => {
            onPhase(id);
          }}
          ariaLabel={`${title} phase`}
        />
      </div>
      <div className="mt-4 rounded-[10px] border border-[var(--tcc-border)] bg-[rgba(5,8,16,0.45)] px-3 py-2.5">
        <GradeVector grade={bridge.grade} compact label={`${title} grade`} />
      </div>
    </PanelSurface.Card>
  );
}
